/**
 * Generated by orval 🍺
 * Do not edit manually.
 * ILP Content Delivery API
 * API for delivering educational content including course structures and glossaries
 * OpenAPI spec version: v1
 */
import useSwr from "swr";
import type { Key, SWRConfiguration } from "swr";

import type {
  Error,
  Glossary,
  ListPreviewStoryboardsParams,
  PreviewCourseStructure,
  Storyboard,
} from "../model";

import { apiFetch } from "../../apiFetch";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

export const getGetPreviewCourseStructureUrl = (externalId: string) => {
  return `/api/v1/preview/courses/${externalId}`;
};

/**
 * The course structure as it currently is in the Content Factory, unlike the snapshot an enrollment serves. Requires the preview permission (admin, DLD, SME) or the system token.
 * @summary Preview the live course structure
 */
export const getPreviewCourseStructure = async (
  externalId: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<PreviewCourseStructure> => {
  return apiFetch<PreviewCourseStructure>(
    getGetPreviewCourseStructureUrl(externalId),
    {
      ...options,
      method: "GET",
    }
  );
};

export const getGetPreviewCourseStructureKey = (externalId: string) =>
  [`/api/v1/preview/courses/${externalId}`] as const;

export type GetPreviewCourseStructureQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPreviewCourseStructure>>
>;

/**
 * @summary Preview the live course structure
 */
export const useGetPreviewCourseStructure = <TError = Error>(
  externalId: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getPreviewCourseStructure>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false &&
    externalId !== null &&
    externalId !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getGetPreviewCourseStructureKey(externalId) : null));
  const swrFn = () => getPreviewCourseStructure(externalId, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getListPreviewGlossaryTermsUrl = (courseExternalId: string) => {
  return `/api/v1/preview/courses/${courseExternalId}/glossaries`;
};

/**
 * The glossary terms as they currently are in the Content Factory.
 * @summary Preview the live glossary of a course
 */
export const listPreviewGlossaryTerms = async (
  courseExternalId: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Glossary> => {
  return apiFetch<Glossary>(getListPreviewGlossaryTermsUrl(courseExternalId), {
    ...options,
    method: "GET",
  });
};

export const getListPreviewGlossaryTermsKey = (courseExternalId: string) =>
  [`/api/v1/preview/courses/${courseExternalId}/glossaries`] as const;

export type ListPreviewGlossaryTermsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPreviewGlossaryTerms>>
>;

/**
 * @summary Preview the live glossary of a course
 */
export const useListPreviewGlossaryTerms = <TError = Error>(
  courseExternalId: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listPreviewGlossaryTerms>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false &&
    courseExternalId !== null &&
    courseExternalId !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() =>
      isEnabled ? getListPreviewGlossaryTermsKey(courseExternalId) : null);
  const swrFn = () =>
    listPreviewGlossaryTerms(courseExternalId, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getListPreviewStoryboardsUrl = (
  params: ListPreviewStoryboardsParams
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/preview/storyboards?${stringifiedParams}`
    : `/api/v1/preview/storyboards`;
};

/**
 * The storyboards of a subchapter as they currently are in the Content Factory, prepared for the client like the delivered ones (quiz answers shuffled, answer keys stripped).
 * @summary Preview the live storyboards of a subchapter
 */
export const listPreviewStoryboards = async (
  params: ListPreviewStoryboardsParams,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Storyboard[]> => {
  return apiFetch<Storyboard[]>(getListPreviewStoryboardsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListPreviewStoryboardsKey = (
  params: ListPreviewStoryboardsParams
) => [`/api/v1/preview/storyboards`, ...(params ? [params] : [])] as const;

export type ListPreviewStoryboardsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPreviewStoryboards>>
>;

/**
 * @summary Preview the live storyboards of a subchapter
 */
export const useListPreviewStoryboards = <TError = unknown>(
  params: ListPreviewStoryboardsParams,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof listPreviewStoryboards>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled = swrOptions?.enabled !== false;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getListPreviewStoryboardsKey(params) : null));
  const swrFn = () => listPreviewStoryboards(params, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
export const getGetPreviewStoryboardUrl = (id: string) => {
  return `/api/v1/preview/storyboards/${id}`;
};

/**
 * One storyboard as it currently is in the Content Factory, prepared for the client like the delivered one.
 * @summary Preview one live storyboard
 */
export const getPreviewStoryboard = async (
  id: string,
  options?: Parameters<typeof apiFetch>[1]
): Promise<Storyboard> => {
  return apiFetch<Storyboard>(getGetPreviewStoryboardUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetPreviewStoryboardKey = (id: string) =>
  [`/api/v1/preview/storyboards/${id}`] as const;

export type GetPreviewStoryboardQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPreviewStoryboard>>
>;

/**
 * @summary Preview one live storyboard
 */
export const useGetPreviewStoryboard = <TError = Error>(
  id: string,
  options?: {
    swr?: SWRConfiguration<
      Awaited<ReturnType<typeof getPreviewStoryboard>>,
      TError
    > & { swrKey?: Key; enabled?: boolean };
    request?: SecondParameter<typeof apiFetch>;
  }
) => {
  const { swr: swrOptions, request: requestOptions } = options ?? {};

  const isEnabled =
    swrOptions?.enabled !== false && id !== null && id !== undefined;
  const swrKey =
    swrOptions?.swrKey ??
    (() => (isEnabled ? getGetPreviewStoryboardKey(id) : null));
  const swrFn = () => getPreviewStoryboard(id, requestOptions);

  const query = useSwr<Awaited<ReturnType<typeof swrFn>>, TError>(
    swrKey,
    swrFn,
    swrOptions
  );

  return {
    swrKey,
    ...query,
  };
};
